import React, { createContext, useContext, useState } from 'react';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'info', duration = 3000) => {
    setNotification({ message, type });

    // Auto-clear after duration
    setTimeout(() => {
      setNotification(null);
    }, duration);
  };

  const showSuccess = (message, duration = 3000) => {
    showNotification(message, 'success', duration);
  };

  const showError = (message, duration = 4000) => {
    showNotification(message, 'error', duration);
  };

  const showWarning = (message, duration = 4000) => {
    showNotification(message, 'warning', duration);
  };

  const showInfo = (message, duration = 3000) => {
    showNotification(message, 'info', duration);
  };

  return (
    <NotificationContext.Provider value={{ showNotification, showSuccess, showError, showWarning, showInfo }}>
      {children}
      <Notification notification={notification} />
    </NotificationContext.Provider>
  );
};

const Notification = ({ notification }) => {
  if (!notification) return null;

  const colors = {
    success: 'var(--color-positive)',
    error: 'var(--color-negative)',
    warning: '#f59e0b',
    info: '#3b82f6'
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        animation: 'slideDown 0.3s ease-out',
      }}
    >
      <div
        style={{
          marginTop: '1rem',
          padding: '0.75rem 1.5rem',
          background: 'white',
          color: colors[notification.type] || colors.info,
          borderRadius: '6px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
          border: `1px solid ${colors[notification.type] || colors.info}`,
          fontSize: '0.875rem',
          fontWeight: '500',
          whiteSpace: 'nowrap',
        }}
      >
        {notification.message}
      </div>
      <style>
        {`
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateX(-50%) translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateX(-50%) translateY(0);
            }
          }
        `}
      </style>
    </div>
  );
};
